import { Component } from '@theme/component';

class CountUp extends Component {
  /** Fallback when `data-duration` is absent or unparseable, in milliseconds. */
  static DEFAULT_DURATION = 2000;

  /** Share of the element that must be visible before counting starts. */
  static VISIBILITY_THRESHOLD = 0.6;

  /** The first number in the figure, with any thousands separators kept. */
  static NUMBER = /-?\d[\d, \s]*(?:\.\d+)?/;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number} rAF handle, 0 when nothing is scheduled. */
  #frame = 0;

  /** @type {CountUpFigure | null} */
  #figure = null;

  /** @type {boolean} */
  #started = false;

  connectedCallback() {
    super.connectedCallback();

    this.#figure = this.#parse();
    if (!this.#figure) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#start();
        }
      },
      { threshold: CountUp.VISIBILITY_THRESHOLD }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = null;

    if (this.#frame) {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
      // Interrupted mid-count — leave the real figure behind, not a frame of it.
      if (this.#figure) this.#render(this.#figure.target);
    }
  }

  /** Duration of the count, in milliseconds. @returns {number} */
  get #duration() {
    const value = Number.parseInt(this.dataset.duration ?? '', 10);
    return Number.isFinite(value) && value >= 0 ? value : CountUp.DEFAULT_DURATION;
  }

  /**
   * The text node the figure is written to. Writing `nodeValue` rather than
   * `textContent` keeps the base class's ref MutationObserver quiet — it does
   * not watch `characterData` — so a 2-second count does not re-sync refs on
   * every frame.
   *
   * @returns {Text | null}
   */
  get #textNode() {
    const display = this.refs.display;
    if (!display) return null;

    const node = display.firstChild;
    if (node instanceof Text) return node;

    const created = document.createTextNode('');
    display.replaceChildren(created);
    return created;
  }

  /**
   * Reads the final figure out of the markup.
   *
   * @returns {CountUpFigure | null} `null` when the figure holds no number.
   */
  #parse() {
    const text = this.refs.display?.textContent ?? '';
    const match = text.match(CountUp.NUMBER);
    if (!match || match.index == null) return null;

    // `[\d,\s]*` is greedy, so a figure like `1 200 units` swallows the space
    // that belongs to the suffix. Hand it back.
    const trailing = match[0].match(/[, \s]+$/);
    const raw = trailing ? match[0].slice(0, -trailing[0].length) : match[0];

    const digits = raw.replace(/[^\d.-]/g, '');
    const target = Number.parseFloat(digits);
    if (!Number.isFinite(target)) return null;

    const dot = digits.indexOf('.');
    const separator = raw.match(/[, \s]/);

    return {
      prefix: text.slice(0, match.index),
      suffix: text.slice(match.index + raw.length),
      target,
      decimals: dot < 0 ? 0 : digits.length - dot - 1,
      group: separator?.[0] ?? '',
    };
  }

  #start() {
    if (this.#started) return;
    this.#started = true;

    this.#observer?.disconnect();
    this.#observer = null;

    const figure = this.#figure;
    if (!figure) return;

    this.#lockWidth();

    const duration = this.#duration;
    if (duration === 0) return; // No count asked for — the final figure stands.

    const startedAt = performance.now();

    const step = (/** @type {number} */ now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      this.#render(figure.target * easeOut(progress));
      this.#frame = progress < 1 ? requestAnimationFrame(step) : 0;
    };

    this.#render(0);
    this.#frame = requestAnimationFrame(step);
  }

  /**
   * Pins the element to the width of its final figure, so the surrounding
   * layout does not move as the digit count grows.
   */
  #lockWidth() {
    const { width } = this.getBoundingClientRect();
    if (width > 0) this.style.setProperty('--count-up-min-width', `${width.toFixed(2)}px`);
  }

  /**
   * Writes a value back in the figure's own shape.
   *
   * @param {number} value
   */
  #render(value) {
    const figure = this.#figure;
    const node = this.#textNode;
    if (!figure || !node) return;

    const { prefix, suffix, decimals, group } = figure;
    let digits = Math.abs(value).toFixed(decimals);

    if (group) {
      const [whole, fraction] = digits.split('.');
      digits = (whole ?? '').replace(/\B(?=(?:\d{3})+$)/g, group) + (fraction ? `.${fraction}` : '');
    }

    node.nodeValue = `${prefix}${value < 0 ? '-' : ''}${digits}${suffix}`;
  }
}

/**
 * Decelerating ease — the count sprints, then settles on the figure.
 *
 * @param {number} t - Progress, 0 to 1.
 * @returns {number}
 */
const easeOut = (t) => 1 - (1 - t) ** 3;

if (!customElements.get('count-up')) {
  customElements.define('count-up', CountUp);
}
